import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  Modal,
  Popover,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ModeEditOutlineRoundedIcon from '@mui/icons-material/ModeEditOutlineRounded';
import RedeemIcon from '@mui/icons-material/Redeem';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded';
import {
  CALCULATOR_API_URL,
  COLOR_GREEN,
  COLOR_PRIMARY,
  COLOR_RED,
  CountryCodeWithCurrency,
  CurrencyWithSubscriptionPrice,
  exchangeRates,
  MID_GREY,
  YEARLY_UPGRADE_DISCOUNT_INVALID_FOR_STATUS,
} from 'src/utils/const';
import {
  COLOR_GREY_600,
  COLOR_GREY_800,
  COLOR_PRIMARY_1_100,
  COLOR_PRIMARY_2,
  COLOR_PRIMARY_2_100,
  COLOR_SUCCESS,
  COLOR_TEXT_1,
  COLOR_TEXT_2,
  COLOR_TEXT_3,
  COLOR_TEXT_4,
  COLOR_TEXT_GREY,
} from 'src/utils/colors';

import GiftImage from 'src/App/assets/gift-svg.svg';
import { callApi } from 'src/core/callApi';
import { formatCurrency, formatDate, getYearlyUpgradeFinalDueAmount } from 'src/utils/functions';
import { useSelector } from 'react-redux';
import { User } from 'src/interfaces/iuser';
import FullScreenLoader from 'src/App/Dashboards/Market/Components/Loaders/FullScreenAnimationLoader';
import { getYearlyUpgradeSubscriptionDateString, unsubscribeUser } from './helper';
import ActiveSubscriptionCard from './ActiveSubscriptionCard';
import { CircularProgressInText, NewSubscriptionDateText } from './CommonComponents';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

const CancellationSurveyModal = ({
  isMobileView,
  setIsModalOpen,
  userSubscriptionDetails,
  setShowUpgradeYearlySubscriptionModal,
  userReportCount,
  setNotification,
  finalDueAmount,
}: {
  isMobileView: boolean;
  setIsModalOpen;
  userSubscriptionDetails;
  setShowUpgradeYearlySubscriptionModal;
  userReportCount: number | null;
  setNotification: React.Dispatch<React.SetStateAction<any>>;
  finalDueAmount: number | null;
}) => {
  const [cancellationStep, setCancellationStep] = useState<0 | 1 | 2>(0);
  const [exitSurveyOptionSelected, setExitSurveyOptionSelected] = useState<string | null>(null);
  const [customExitSurvey, setCustomExitSurvey] = useState<string>('');

  const [isSubscriptionCancelling, setIsSubscriptionCancelling] = useState<boolean>(false);

  const userSubscriptionPlan = useSelector((state: any) => state.dashboard.user.subscriptionPlan);

  const lostAccessBenefits = [
    `You’ll lose ${
      userReportCount && userReportCount > 0
        ? `access to ${userReportCount} Airbnb Revenue Reports and`
        : ''
    } access to unlimited Airbnb Revenue Reports creation.`,
    'You’ll lose access to unlimited access to Airbnb Market Intelligence.',
  ];

  const paymentFrequency = userSubscriptionDetails['payment_frequency'];
  const subscriptionPlan = userSubscriptionDetails['subscription_plan'];

  if (subscriptionPlan == 'scale') {
    lostAccessBenefits.push(
      'You’ll lose access to unlimited access to listing-level monthly historical performance data.'
    );
  }

  const exitSurvey = [
    'I don’t need it anymore.',
    'Too expensive.',
    'Low quality.',
    'Switching to another service.',
    'Missing features.',
    'Others',
  ];

  const ExitSurveyUI = (
    <>
      <FormControl fullWidth>
        <RadioGroup
          value={exitSurveyOptionSelected}
          onChange={event => setExitSurveyOptionSelected(event.target.value)}
        >
          {exitSurvey.map(surveyText => (
            <FormControlLabel value={surveyText} control={<Radio />} label={surveyText} />
          ))}
        </RadioGroup>
      </FormControl>

      {exitSurveyOptionSelected === 'Others' && (
        <TextField
          variant="standard"
          value={customExitSurvey}
          sx={{ width: '280px' }}
          onChange={event => setCustomExitSurvey(event.target.value)}
        />
      )}
    </>
  );

  const LossOfBenifitsUI = (
    <>
      <Box sx={{ background: COLOR_PRIMARY, borderRadius: '12px', padding: '20px' }}>
        {lostAccessBenefits.map(lostAccess => (
          <Stack spacing={2}>
            <Stack
              spacing={2}
              direction="row"
              alignItems="center"
              sx={{ color: '#fff', fontSize: '18px', mb: '10px' }}
            >
              <ArrowForwardIosRoundedIcon sx={{ fontSize: '16px' }} />
              <Typography variant="body1">{lostAccess}</Typography>
            </Stack>
          </Stack>
        ))}
      </Box>
    </>
  );

  const userSignupCountryCode = useSelector((state: any) => state.dashboard.user.signupCountryCode);
  const userLocalCurrency = CountryCodeWithCurrency[userSignupCountryCode] || 'USD';
  // const subscriptionPrice = formatCurrency(
  //   String(CurrencyWithSubscriptionPrice[userLocalCurrency][subscriptionPlan]['monthly'])
  // );
  const currencySymbol = exchangeRates[userLocalCurrency].symbol;

  const SpecialOfferUI = (
    <Stack spacing={2} justifyContent={'center'} sx={{ mt: '-40px', px: '10%' }}>
      <img
        src={GiftImage}
        alt="Special Offer"
        style={{
          maxWidth: '150px',
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      />
      <Typography
        variant="body1"
        textAlign={'center'}
        sx={{ fontSize: '24px', color: COLOR_TEXT_1 }}
      >
        We’d love to make Airbtics work for you!{' '}
        <strong style={{ fontWeight: 500 }}>Your next 6 months on us!</strong> Pay extra{' '}
        {currencySymbol}
        {finalDueAmount ? formatCurrency(String(finalDueAmount)) : CircularProgressInText} for
        additional 12 months.
      </Typography>
      <Typography variant="body2" sx={{ color: COLOR_TEXT_GREY }}>
        Enjoy powerful short-term rental insights and exclusive features available only to
        Expert/Scale members.
      </Typography>
      {NewSubscriptionDateText}
    </Stack>
  );

  const getActionButtonUI = btnObj => {
    return (
      <Button
        variant="contained"
        onClick={btnObj['action']}
        disabled={btnObj.isDisabled ? true : false}
        sx={{
          color: btnObj['color'],
          background: btnObj['background'],
          maxWidth: '300px',
          '&:hover': {
            background: btnObj['background'] + '88',
          },
        }}
      >
        {btnObj['title']}
      </Button>
    );
  };

  const handleExitSurveySubmission = async () => {
    const URL = 'https://fld2j6mes8.execute-api.us-east-2.amazonaws.com/default/updateUserDetails';
    const cancellationReason =
      exitSurveyOptionSelected === 'Others' ? customExitSurvey : exitSurveyOptionSelected;
    const cancellationReasonSaved = await callApi(
      URL,
      {
        queryParams: {
          cancellationReason: cancellationReason,
        },
      },
      true,
      false,
      '',
      'GET',
      true
    );
  };

  const handleUnsubscription = async () => {
    setIsSubscriptionCancelling(true);
    await handleExitSurveySubmission();
    const isUnsubscribed = await unsubscribeUser();

    if (isUnsubscribed) {
      setNotification(
        <Alert
          onClose={() => setNotification(null)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Your subscription has been successfully cancelled.
        </Alert>
      );
      setTimeout(() => {
        window.location.reload();
      }, 3000);
    } else {
      console.log('Unsubscription Failed!');
    }

    setIsSubscriptionCancelling(false);
  };

  const cancellationFlows = [
    {
      title: 'Are you sure you want to cancel your subscription?',
      body: LossOfBenifitsUI,
      actionButtons: [
        {
          title: 'Continue Cancellation',
          action: () => {
            setCancellationStep(paymentFrequency === 'monthly' &&
              userSubscriptionPlan !== 'starter' &&
              !YEARLY_UPGRADE_DISCOUNT_INVALID_FOR_STATUS.includes(
                userSubscriptionDetails.subscription_status
              )
              ? 1
              : 2);
          },
          color: COLOR_TEXT_GREY,
          background: COLOR_GREY_800,
        },
        {
          title: `Keep Airbtics ${subscriptionPlan}`,
          action: () => {
            setIsModalOpen(false);
          },
          color: '#fff',
          background: COLOR_PRIMARY_2,
        },
      ],
    },
    {
      body: SpecialOfferUI,
      actionButtons: [
        {
          title: 'Continue Cancellation',
          action: () => {
            setCancellationStep(2);
          },
          color: COLOR_TEXT_GREY,
          background: COLOR_GREY_800,
        },
        {
          title: 'Claim It',
          action: () => {
            setShowUpgradeYearlySubscriptionModal(true);
            setIsModalOpen(false);
          },
          color: '#fff',
          background: COLOR_PRIMARY_2,
        },
      ],
      placeActionButtonCenter: true,
      footerText:
        'After the promotional period, you will return to your original price unless you choose to cancel.',
    },
    {
      title: 'Please let us know why do you want to cancel ?',
      body: ExitSurveyUI,
      actionButtons: [
        {
          title: 'Unsubscribe',
          action: () => {
            handleUnsubscription();
          },
          color: COLOR_TEXT_GREY,
          background: COLOR_GREY_800,
          isDisabled: exitSurveyOptionSelected ? false : true,
        },
        {
          title: `Keep Airbtics ${subscriptionPlan}`,
          action: () => {
            setIsModalOpen(false);
          },
          color: '#fff',
          background: COLOR_PRIMARY_2,
        },
      ],
    },
  ];

  return (
    <>
      <Modal open={true} onClose={() => setIsModalOpen(false)}>
        <Box
          sx={{
            width: isMobileView ? '100dvw' : '70dvw',
            height: '80dvh',
            background: '#fff',
            borderRadius: '8px',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            outline: 'none',
            border: 'none',
          }}
        >
          <Card sx={{ height: '100%', overflow: 'auto' }}>
            <CardHeader
              action={
                <Box sx={{ display: 'inline-box' }}>
                  <IconButton onClick={() => setIsModalOpen(false)}>
                    <CloseRoundedIcon sx={{ color: COLOR_RED }} />
                  </IconButton>
                </Box>
              }
              title="Subscription Cancellation"
              sx={{
                paddingBottom: 0,
                '.MuiCardHeader-title': {
                  fontSize: '24px',
                  fontWeight: 500,
                  color: '#000',
                },
              }}
            />
            <Divider sx={{ my: '10px' }} />
            <CardContent>
              {cancellationFlows[cancellationStep]['title'] && (
                <Typography variant="h5" sx={{ color: COLOR_TEXT_1 }}>
                  {cancellationFlows[cancellationStep]['title']}
                </Typography>
              )}
              {cancellationFlows[cancellationStep]['body'] && (
                <Typography variant="body1" sx={{ mt: '30px' }}>
                  {cancellationFlows[cancellationStep]['body']}
                </Typography>
              )}
            </CardContent>
            <CardActions
              sx={{
                my: '20px',
                justifyContent: cancellationFlows[cancellationStep]['placeActionButtonCenter']
                  ? 'center'
                  : 'flex-end',
              }}
            >
              {cancellationFlows[cancellationStep]['actionButtons']?.map(btnObj =>
                getActionButtonUI(btnObj)
              )}
            </CardActions>
            {cancellationFlows[cancellationStep]['footerText'] && (
              <Typography
                variant="body2"
                textAlign={'center'}
                sx={{ mt: '10px', color: COLOR_TEXT_GREY }}
              >
                {cancellationFlows[cancellationStep]['footerText']}
              </Typography>
            )}
          </Card>
        </Box>
      </Modal>
      {isSubscriptionCancelling && <FullScreenLoader />}
    </>
  );
};

const LoadingCard = () => {
  return (
    <Card sx={{ border: 'none', boxShadow: 'none' }}>
      <CardContent
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100px' }}
      >
        <CircularProgress size={60} />
      </CardContent>
    </Card>
  );
};

const NoDataFound = () => {
  return <Typography variant="body1">You don't have any subscriptions.</Typography>;
};

const BillingTab = ({
  isMobileView,
  setShowUpgradeYearlySubscriptionModal,
}: {
  isMobileView: boolean;
  setShowUpgradeYearlySubscriptionModal;
}) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [userSubscriptionDetails, setUserSubscriptionDetails] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [finalDueAmount, setFinalDueAmount] = useState<number | null>(null);

  const [notification, setNotification] = useState<any>(null);
  const user: User = useSelector(state => (state as any).dashboard.user);
  const userSubscriptionPlan = useSelector((state: any) => state.dashboard.user.subscriptionPlan);
  const [userReportCount, setUserReportCount] = useState<number | null>(null);

  const userSignupCountryCode = useSelector((state: any) => state.dashboard.user.signupCountryCode);
  const userSubscriptionCurrency = CountryCodeWithCurrency[userSignupCountryCode] || 'USD';
  const currencySymbol = exchangeRates[userSubscriptionCurrency].symbol;

  const IS_TEST_MODE: boolean = false;

  useEffect(() => {
    if (
      userSubscriptionDetails &&
      userSubscriptionDetails.length &&
      userSubscriptionPlan !== 'starter' &&
      !YEARLY_UPGRADE_DISCOUNT_INVALID_FOR_STATUS.includes(
        userSubscriptionDetails[0]?.subscription_status
      )
    ) {
      const stripeAccount = (user as any)?.stripeAccount || 'airbtics_uk';
      getYearlyUpgradeFinalDueAmount(
        user.subscriptionPlan,
        userSubscriptionCurrency,
        setFinalDueAmount,
        { isTestMode: IS_TEST_MODE, stripeAccount }
      );
    }
  }, [userSubscriptionDetails]);

  const getTotalReportCreated = async () => {
    const URL = CALCULATOR_API_URL;
    const data = await callApi(URL, {}, true, false, '', 'GET', true);
    console.log('Data: ', data.length);
    data !== 'Unauthenticated request' ? setUserReportCount(data.length) : setUserReportCount(0);
  };

  useEffect(() => {
    const URL =
      'https://w2g9kh7lf4.execute-api.us-east-2.amazonaws.com/default/fetchCustomerSubscriptionDetails';

    const getUserSubscriptionDetails = async () => {
      const userDetails = await callApi(
        URL,
        {
          queryParams: {
            isTest: IS_TEST_MODE,
            returnAllSubscriptions: true,
          },
        },
        true,
        false,
        '',
        'GET',
        true
      );
      console.log(userDetails);
      userDetails &&
        !userDetails.error &&
        userDetails.subscriptions &&
        userDetails.subscriptions.length > 0 &&
        setUserSubscriptionDetails(userDetails.subscriptions);

      setIsLoading(false);
    };

    if (user.email !== 'Trial') {
      getUserSubscriptionDetails();
      getTotalReportCreated();
    }
  }, [user]);

  return (
    <>
      {/* <Grid item xs={12} sm={12} md={6} lg={6} xl={6}> */}
      <Grid item xs={12}>
        {isLoading ? (
          <LoadingCard />
        ) : !userSubscriptionDetails || userSubscriptionDetails.length === 0 ? (
          <NoDataFound />
        ) : (
          <ActiveSubscriptionCard
            setIsModalOpen={setIsModalOpen}
            userSubscriptionDetails={userSubscriptionDetails[0]}
            setShowUpgradeYearlySubscriptionModal={setShowUpgradeYearlySubscriptionModal}
            finalDueAmount={finalDueAmount}
          />
        )}
      </Grid>
      {isModalOpen && (
        <CancellationSurveyModal
          isMobileView={isMobileView}
          setIsModalOpen={setIsModalOpen}
          userSubscriptionDetails={userSubscriptionDetails[0]}
          setShowUpgradeYearlySubscriptionModal={setShowUpgradeYearlySubscriptionModal}
          userReportCount={userReportCount}
          setNotification={setNotification}
          finalDueAmount={finalDueAmount}
        />
      )}

      {userSubscriptionDetails && userSubscriptionDetails.length > 1 && (
        <Accordion sx={{ mt: '20px', width: '100%', boxShadow: 'none' }}>
          <AccordionSummary
            expandIcon={<ExpandMoreRoundedIcon />}
            aria-controls="subscription-history-content"
            id="subscription-history-header"
          >
            <Typography variant="h5">Subscription History</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid item xs={12}>
              {userSubscriptionDetails.slice(1).map((subscription: any) => (
                <ActiveSubscriptionCard
                  setIsModalOpen={setIsModalOpen}
                  userSubscriptionDetails={subscription}
                  setShowUpgradeYearlySubscriptionModal={setShowUpgradeYearlySubscriptionModal}
                  finalDueAmount={finalDueAmount}
                  hideUpgradeButton={true}
                />
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      {notification && (
        <Snackbar
          open={Boolean(notification)}
          autoHideDuration={3000}
          onClose={() => setNotification(null)}
        >
          {notification}
        </Snackbar>
      )}
    </>
  );
};

export default BillingTab;
